'use client';

import { type ReactNode, useState, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import type { FeedbackButtonLabels } from './FeedbackButton';

export type FeedbackType = 'bug' | 'feature_request' | 'question' | 'other';

export interface FeedbackFormData {
  type: FeedbackType;
  description: string;
  email?: string;
  screenshot?: File;
}

export interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FeedbackFormData) => Promise<void>;
  labels: Required<FeedbackButtonLabels>;
  showScreenshot?: boolean;
  showTypeSelector?: boolean;
  /** Pre-fill the email field (e.g. from authenticated user) */
  defaultEmail?: string;
}

const FEEDBACK_TYPES: FeedbackType[] = ['bug', 'feature_request', 'question', 'other'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function FeedbackModal({
  open,
  onClose,
  onSubmit,
  labels,
  showScreenshot = true,
  showTypeSelector = true,
  defaultEmail = '',
}: FeedbackModalProps): ReactNode {
  const [type, setType] = useState<FeedbackType>('bug');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState(defaultEmail);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const descriptionValid = description.trim().length >= 10;
  const canSubmit = descriptionValid && !submitting;

  function resetForm() {
    setType('bug');
    setDescription('');
    setEmail(defaultEmail);
    setScreenshot(null);
    setScreenshotPreview(null);
    setStatus('idle');
    setFileError(null);
  }

  function handleClose() {
    if (!submitting) {
      resetForm();
      onClose();
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setFileError(labels.fileTooLarge);
      return;
    }

    setFileError(null);
    setScreenshot(file);
    const url = URL.createObjectURL(file);
    setScreenshotPreview(url);
  }

  function removeScreenshot() {
    setScreenshot(null);
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
      setScreenshotPreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setStatus('idle');

    try {
      const data: FeedbackFormData = {
        type,
        description: description.trim(),
      };
      const trimmedEmail = email.trim();
      if (trimmedEmail) data.email = trimmedEmail;
      if (screenshot) data.screenshot = screenshot;

      await onSubmit(data);
      setStatus('success');
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
    } catch {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="feedback-dialog-title"
    >
      <DialogTitle id="feedback-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {labels.title}
        <IconButton
          aria-label={labels.cancel}
          onClick={handleClose}
          disabled={submitting}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {status === 'success' && (
            <Alert severity="success">{labels.success}</Alert>
          )}
          {status === 'error' && (
            <Alert severity="error">{labels.error}</Alert>
          )}

          {showTypeSelector && (
            <TextField
              select
              label={labels.typeLabel}
              value={type}
              onChange={(e) => setType(e.target.value as FeedbackType)}
              fullWidth
              size="small"
              disabled={submitting}
            >
              {FEEDBACK_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {labels.types[t] || t}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            label={labels.descriptionLabel}
            placeholder={labels.descriptionPlaceholder}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={4}
            fullWidth
            required
            size="small"
            disabled={submitting}
            error={description.length > 0 && !descriptionValid}
            helperText={description.length > 0 && !descriptionValid ? labels.descriptionMinLength : undefined}
          />

          <TextField
            label={labels.emailLabel}
            placeholder={labels.emailPlaceholder}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
            disabled={submitting}
            helperText={labels.emailHelper}
          />

          {showScreenshot && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {labels.screenshotLabel}
              </Typography>

              {screenshotPreview ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Box
                    component="img"
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={removeScreenshot}
                    aria-label={labels.removeScreenshot}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      insetInlineEnd: 4,
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PhotoCameraIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={submitting}
                  >
                    {labels.uploadScreenshot}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </>
              )}

              {fileError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {fileError}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={submitting}>
            {labels.cancel}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? labels.submitting : labels.submit}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

FeedbackModal.displayName = 'FeedbackModal';
